import { useEffect, useState } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, Trash2, Smartphone, Banknote, CheckCircle2, Loader2, Copy, Upload } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = { name: "", email: "", phone: "", address: "", city: "", postal_code: "", country: "" };
const UPI_QR_URL = "/images/qr.jpg";
const UPI_ID = "sethia.richa@okhdfcbank";

export const CartDrawer = () => {
    const { items, cartOpen, setCartOpen, updateQty, removeItem, total, clearCart } = useCart();
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(null);
    const [order, setOrder] = useState(null);
    const [showUpi, setShowUpi] = useState(false);
    const [rzConfig, setRzConfig] = useState(null);
    const [proofPath, setProofPath] = useState("");
    const [shoeNumber, setShoeNumber] = useState(null);
    const [proofPreview, setProofPreview] = useState("");
    const [proofUploading, setProofUploading] = useState(false);

    useEffect(() => {
        if (checkoutOpen && !rzConfig) {
            axios
                .get(`${API}/payments/config`)
                .then((r) => setRzConfig(r.data))
                .catch(() => setRzConfig({ razorpay_enabled: false }));
        }
    }, [checkoutOpen, rzConfig]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const payload = () => ({
        items: items.map((i) => ({ product_id: i.productId, size: i.size, qty: i.qty })),
        customer: form,
        origin_url: window.location.origin,
        payment_proof: proofPath,
        custom_number: shoeNumber,
    });

    const uploadProof = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Only image files allowed");
            return;
        }
        setProofUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(`${API}/uploads/payment-proof`, fd);
            setProofPath(data.path);
            setProofPreview(URL.createObjectURL(file));
            toast.success("Payment screenshot attached");
        } catch {
            toast.error("Upload failed — try again");
        } finally {
            setProofUploading(false);
        }
    };

    const needsNumber = items.some((i) => i.productId === "play-beyond-004");

    const valid =
        form.name &&
        /\S+@\S+\.\S+/.test(form.email) &&
        form.address &&
        form.city &&
        form.country &&
        (!needsNumber || shoeNumber);

    const payCod = async () => {
        setLoading("cod");
        try {
            const { data } = await axios.post(`${API}/orders/cod`, payload());
            setOrder(data);
            clearCart();
        } catch {
            toast.error("Could not place order — try again");
        } finally {
            setLoading(null);
        }
    };

    const confirmUpi = async () => {
        setLoading("upi");
        try {
            const { data } = await axios.post(`${API}/orders/upi`, payload());
            setOrder(data);
            clearCart();
        } catch {
            toast.error("Could not place order — try again");
        } finally {
            setLoading(null);
        }
    };

    const loadRazorpayScript = () =>
        new Promise((resolve) => {
            if (window.Razorpay) return resolve(true);
            const s = document.createElement("script");
            s.src = "https://checkout.razorpay.com/v1/checkout.js";
            s.onload = () => resolve(true);
            s.onerror = () => resolve(false);
            document.body.appendChild(s);
        });

    const payRazorpay = async () => {
        setLoading("online");
        try {
            const ok = await loadRazorpayScript();
            if (!ok) throw new Error("script");
            const { data } = await axios.post(`${API}/payments/razorpay/order`, payload());
            const rzp = new window.Razorpay({
                key: data.key_id,
                amount: data.amount_paise,
                currency: "INR",
                name: "Vault Deportivos",
                description: data.order_id,
                order_id: data.razorpay_order_id,
                prefill: { name: form.name, email: form.email, contact: form.phone },
                theme: { color: "#a8853a" },
                modal: { ondismiss: () => setLoading(null) },
                handler: async (res) => {
                    try {
                        const v = await axios.post(`${API}/payments/razorpay/verify`, {
                            order_id: data.order_id,
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                        });
                        setOrder(v.data);
                        clearCart();
                    } catch {
                        toast.error("Payment verification failed — contact the studio");
                    } finally {
                        setLoading(null);
                    }
                },
            });
            rzp.open();
        } catch {
            toast.error("Could not start payment — try again");
            setLoading(null);
        }
    };

    const handlePayOnline = () => {
        if (rzConfig?.razorpay_enabled) {
            payRazorpay();
        } else {
            setShowUpi(true);
        }
    };

    const closeAll = () => {
        setCheckoutOpen(false);
        setCartOpen(false);
        setOrder(null);
        setShowUpi(false);
        setProofPath("");
        setProofPreview("");
        setShoeNumber(null);
        setForm(EMPTY_FORM);
    };

    return (
        <>
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
                <SheetContent
                    data-testid="cart-drawer"
                    side="right"
                    className="w-full border-vault-border bg-vault-black text-stone-900 sm:max-w-md"
                >
                    <SheetHeader>
                        <SheetTitle className="font-display text-2xl uppercase tracking-wide text-stone-900">
                            Your Cart
                        </SheetTitle>
                    </SheetHeader>
                    <div className="mt-8 flex h-[calc(100%-8rem)] flex-col">
                        {items.length === 0 ? (
                            <p data-testid="cart-empty-message" className="font-mono text-xs tracking-[0.2em] text-stone-500">
                                CART IS EMPTY — GO CLAIM A PAIR
                            </p>
                        ) : (
                            <>
                                <div className="flex-1 space-y-6 overflow-y-auto pr-2">
                                    {items.map((item) => (
                                        <div key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-4 border border-vault-border p-3">
                                            <div className="h-20 w-20 shrink-0 border border-vault-border bg-white">
                                                <img src={item.image} alt={item.name} className="h-full w-full object-contain" />
                                            </div>
                                            <div className="flex flex-1 flex-col justify-between">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div>
                                                        <p className="font-display text-sm uppercase text-stone-900">{item.name}</p>
                                                        <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-stone-500">
                                                            SIZE {item.size.replace("US ", "")}
                                                        </p>
                                                    </div>
                                                    <button
                                                        data-testid={`remove-item-${item.key}`}
                                                        aria-label="Remove item"
                                                        onClick={() => removeItem(item.key)}
                                                        className="text-stone-400 transition-colors hover:text-vault-red"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center border border-vault-border">
                                                        <button
                                                            data-testid={`qty-minus-${item.key}`}
                                                            aria-label="Decrease quantity"
                                                            onClick={() => updateQty(item.key, -1)}
                                                            className="px-2 py-1 text-stone-600 hover:text-stone-900"
                                                        >
                                                            <Minus size={12} />
                                                        </button>
                                                        <span className="px-2 font-mono text-xs">{item.qty}</span>
                                                        <button
                                                            data-testid={`qty-plus-${item.key}`}
                                                            aria-label="Increase quantity"
                                                            onClick={() => updateQty(item.key, 1)}
                                                            className="px-2 py-1 text-stone-600 hover:text-stone-900"
                                                        >
                                                            <Plus size={12} />
                                                        </button>
                                                    </div>
                                                    <p className="font-mono text-sm text-stone-900">₹{(item.price * item.qty).toLocaleString("en-IN")}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-6 border-t border-vault-border pt-6">
                                    <div className="mb-4 flex items-center justify-between font-mono text-sm">
                                        <span className="tracking-[0.2em] text-stone-500">SUBTOTAL</span>
                                        <span data-testid="cart-subtotal" className="text-stone-900">₹{total.toLocaleString("en-IN")}</span>
                                    </div>
                                    <button
                                        data-testid="checkout-button"
                                        onClick={() => setCheckoutOpen(true)}
                                        className="btn-sweep w-full border border-vault-red py-4 font-mono text-sm tracking-[0.25em] text-stone-900"
                                    >
                                        CHECKOUT
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
                <DialogContent
                    data-testid="checkout-dialog"
                    className="border-vault-border bg-vault-black text-stone-900 sm:max-w-lg"
                >
                    {order ? (
                        <div data-testid="order-confirmation" className="py-6 text-center">
                            <CheckCircle2 size={48} className="mx-auto text-vault-red" />
                            <h3 className="mt-6 font-display text-3xl uppercase text-stone-900">Order Locked In</h3>
                            <p className="mt-3 font-mono text-sm tracking-[0.2em] text-vault-red">{order.id}</p>
                            {order.custom_number && (
                                <p
                                    data-testid="confirmed-shoe-number"
                                    className="mt-2 font-mono text-xs tracking-[0.2em] text-stone-600"
                                >
                                    NUMBER {order.custom_number} WILL BE PAINTED ON YOUR PAIR
                                </p>
                            )}
                            <p className="mx-auto mt-6 max-w-sm text-sm leading-relaxed text-stone-600">
                                {order.payment_method === "upi" ? (
                                    <>
                                        We&apos;ll verify your UPI payment of{" "}
                                        <span className="text-stone-900">₹{order.total.toLocaleString("en-IN")}</span> and start
                                        painting. Keep your order number handy.
                                    </>
                                ) : order.payment_method === "razorpay" ? (
                                    <>
                                        Payment of{" "}
                                        <span className="text-stone-900">₹{order.total.toLocaleString("en-IN")}</span> received and
                                        verified. Your pair goes under the spray can next — we&apos;ll email you when it ships.
                                    </>
                                ) : (
                                    <>
                                        Pay <span className="text-stone-900">₹{order.total.toLocaleString("en-IN")}</span> in cash when
                                        your pair arrives at {order.customer.city}. We start painting once the order is
                                        in.
                                    </>
                                )}
                            </p>
                            <p className="mx-auto mt-4 max-w-sm font-mono text-[10px] leading-relaxed tracking-[0.15em] text-stone-400">
                                ARRIVES IN 5–7 DAYS · NO CANCELLATIONS · 30-DAY RETURNS · HELP: VAULTDEPORTIVOS@GMAIL.COM
                            </p>
                            <button
                                data-testid="order-confirmation-close"
                                onClick={closeAll}
                                className="btn-sweep mt-8 border border-vault-red px-8 py-3 font-mono text-xs tracking-[0.25em] text-stone-900"
                            >
                                BACK TO THE VAULT
                            </button>
                        </div>
                    ) : showUpi ? (
                        <div data-testid="upi-payment-panel">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl uppercase tracking-wide text-stone-900">
                                    Pay With UPI / GPay
                                </DialogTitle>
                            </DialogHeader>
                            <div className="clip-frame relative mx-auto mt-4 max-w-xs border border-vault-border bg-white p-3">
                                <img
                                    data-testid="upi-qr-image"
                                    src={UPI_QR_URL}
                                    alt="UPI QR code — scan with GPay or any UPI app"
                                    className="w-full"
                                />
                            </div>
                            <div className="mt-5 flex items-center justify-between border border-vault-border px-4 py-3">
                                <div>
                                    <p className="font-mono text-[10px] tracking-[0.25em] text-stone-500">UPI ID</p>
                                    <p data-testid="upi-id-text" className="font-mono text-sm text-stone-900">
                                        {UPI_ID}
                                    </p>
                                </div>
                                <button
                                    data-testid="upi-copy-button"
                                    aria-label="Copy UPI ID"
                                    onClick={() => {
                                        navigator.clipboard.writeText(UPI_ID);
                                        toast.success("UPI ID copied");
                                    }}
                                    className="text-stone-600 transition-colors hover:text-vault-red"
                                >
                                    <Copy size={16} />
                                </button>
                            </div>
                            <div className="mt-4 flex items-center justify-between font-mono text-sm">
                                <span className="tracking-[0.2em] text-stone-500">AMOUNT TO SEND</span>
                                <span data-testid="upi-amount" className="text-stone-900">
                                    ₹{total.toLocaleString("en-IN")}
                                </span>
                            </div>
                            <p className="mt-3 font-mono text-[10px] leading-relaxed tracking-[0.1em] text-stone-400">
                                SCAN WITH GPAY, PHONEPE OR ANY UPI APP, THEN UPLOAD PROOF BELOW
                            </p>
                            <div className="mt-4">
                                <input
                                    data-testid="payment-proof-input"
                                    id="payment-proof-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={uploadProof}
                                    className="hidden"
                                />
                                <label
                                    htmlFor="payment-proof-input"
                                    data-testid="payment-proof-upload"
                                    className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-vault-border px-4 py-5 font-mono text-xs tracking-[0.2em] text-stone-600 transition-colors hover:border-vault-red hover:text-stone-900"
                                >
                                    {proofUploading ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" /> UPLOADING...
                                        </>
                                    ) : proofPath ? (
                                        <>
                                            <CheckCircle2 size={16} className="text-green-400" /> SCREENSHOT ATTACHED — TAP TO
                                            REPLACE
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={16} /> UPLOAD PAYMENT SCREENSHOT
                                        </>
                                    )}
                                </label>
                                {proofPreview && (
                                    <img
                                        data-testid="payment-proof-preview"
                                        src={proofPreview}
                                        alt="Payment screenshot preview"
                                        className="mx-auto mt-3 h-28 border border-vault-border object-contain"
                                    />
                                )}
                            </div>
                            <button
                                data-testid="upi-paid-button"
                                onClick={confirmUpi}
                                disabled={loading !== null || !proofPath || proofUploading}
                                className="btn-sweep mt-5 flex w-full items-center justify-center gap-3 border border-vault-red py-4 font-mono text-sm tracking-[0.25em] text-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {loading === "upi" ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <CheckCircle2 size={16} />
                                )}
                                {proofPath ? "I'VE PAID — PLACE ORDER" : "UPLOAD SCREENSHOT TO CONTINUE"}
                            </button>
                            <button
                                data-testid="upi-back-button"
                                onClick={() => setShowUpi(false)}
                                className="mt-3 w-full py-2 font-mono text-[10px] tracking-[0.3em] text-stone-500 transition-colors hover:text-stone-900"
                            >
                                BACK TO PAYMENT OPTIONS
                            </button>
                        </div>
                    ) : (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl uppercase tracking-wide text-stone-900">
                                    Checkout
                                </DialogTitle>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    ["name", "FULL NAME", "checkout-name-input", "text", 2],
                                    ["email", "EMAIL", "checkout-email-input", "email", 2],
                                    ["phone", "PHONE (OPTIONAL)", "checkout-phone-input", "tel", 2],
                                    ["address", "STREET ADDRESS", "checkout-address-input", "text", 2],
                                    ["city", "CITY", "checkout-city-input", "text", 1],
                                    ["postal_code", "ZIP / POSTAL", "checkout-postal-input", "text", 1],
                                    ["country", "COUNTRY", "checkout-country-input", "text", 2],
                                ].map(([key, label, testid, type, span]) => (
                                    <div key={key} className={span === 2 ? "col-span-2" : ""}>
                                        <label className="mb-1 block font-mono text-[10px] tracking-[0.25em] text-stone-500">
                                            {label}
                                        </label>
                                        <input
                                            data-testid={testid}
                                            type={type}
                                            value={form[key]}
                                            onChange={set(key)}
                                            className="w-full border border-vault-border bg-transparent px-3 py-2.5 text-sm text-stone-900"
                                        />
                                    </div>
                                ))}
                            </div>
                            {needsNumber && (
                                <div
                                    data-testid="shoe-number-box"
                                    className="mt-5 border border-vault-red/40 bg-vault-surface/50 p-4"
                                >
                                    <p className="font-mono text-[10px] tracking-[0.25em] text-vault-red">
                                        CUSTOMIZE YOUR WHITE SHOE — PICK YOUR NUMBER
                                    </p>
                                    <div className="mt-3 grid grid-cols-5 gap-2">
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                                            <button
                                                key={n}
                                                data-testid={`shoe-number-${n}`}
                                                onClick={() => setShoeNumber(n)}
                                                className={`border py-2.5 font-mono text-sm transition-colors ${
                                                    shoeNumber === n
                                                        ? "border-vault-red bg-vault-red text-stone-900"
                                                        : "border-vault-border text-stone-600 hover:border-vault-red"
                                                }`}
                                            >
                                                {n}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="mt-3 font-mono text-[10px] tracking-[0.1em] text-stone-400">
                                        HAND-PAINTED ON YOUR PLAY BEYOND 004
                                    </p>
                                </div>
                            )}

                            <div className="mt-6 flex items-center justify-between border-t border-vault-border pt-4 font-mono text-sm">
                                <span className="tracking-[0.2em] text-stone-500">TOTAL</span>
                                <span data-testid="checkout-total" className="text-stone-900">₹{total.toLocaleString("en-IN")}</span>
                            </div>
                            <p className="mt-4 font-mono text-[10px] tracking-[0.3em] text-stone-500">
                                CHOOSE HOW YOU PAY
                            </p>
                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <button
                                    data-testid="pay-online-button"
                                    onClick={handlePayOnline}
                                    disabled={!valid || loading !== null}
                                    className="btn-sweep flex flex-col items-center gap-2 border border-vault-red px-4 py-5 font-mono text-xs tracking-[0.2em] text-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    {loading === "online" ? <Loader2 size={18} className="animate-spin" /> : <Smartphone size={18} />}
                                    {rzConfig?.razorpay_enabled ? "PAY ONLINE — UPI / CARDS" : "PAY ONLINE — UPI / GPAY"}
                                </button>
                                <button
                                    data-testid="pay-cod-button"
                                    onClick={payCod}
                                    disabled={!valid || loading !== null}
                                    className="flex flex-col items-center gap-2 border border-vault-border px-4 py-5 font-mono text-xs tracking-[0.2em] text-stone-600 transition-colors hover:border-white hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    {loading === "cod" ? <Loader2 size={18} className="animate-spin" /> : <Banknote size={18} />}
                                    CASH ON DELIVERY
                                </button>
                            </div>
                            <p
                                data-testid="checkout-policy-note"
                                className="mt-4 font-mono text-[10px] leading-relaxed tracking-[0.1em] text-stone-400"
                            >
                                MADE TO ORDER — NO CANCELLATIONS · 30-DAY RETURNS · ARRIVES IN 5–7 DAYS · HELP: VAULTDEPORTIVOS@GMAIL.COM
                            </p>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
